import { Input } from "@/components/ui/input";

export interface AllocationRow {
  invoiceId: number;
  invoiceRef: string;
  /** Outstanding balance on the invoice (read-only display). */
  balance: number;
  /** Amount to allocate from this payment to this invoice. */
  allocate: number;
}

export interface PaymentAllocationGridProps {
  rows: AllocationRow[];
  /** Called when the user changes the allocate amount for a row. */
  onAllocateChange: (invoiceId: number, amount: number) => void;
  /** Total payment amount — displayed alongside the running allocation total. */
  paymentTotal?: number;
}

function fmt(n: number) {
  return n.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export function PaymentAllocationGrid({
  rows,
  onAllocateChange,
  paymentTotal,
}: PaymentAllocationGridProps) {
  const runningTotal = rows.reduce((sum, r) => sum + (Number.isFinite(r.allocate) ? r.allocate : 0), 0);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-2 text-left">Invoice</th>
              <th className="p-2 text-right">Balance</th>
              <th className="p-2 text-right w-36">Allocate ($)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-muted-foreground text-sm">
                  No invoices to allocate
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.invoiceId} className="border-t">
                  <td className="p-2 font-mono">{row.invoiceRef}</td>
                  <td className="p-2 text-right">{fmt(row.balance)}</td>
                  <td className="p-2 text-right">
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      aria-label={`Allocate to ${row.invoiceRef}`}
                      value={row.allocate}
                      onChange={(e) =>
                        onAllocateChange(row.invoiceId, Number(e.target.value))
                      }
                      className="w-32 text-right"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot className="border-t bg-muted/30">
            <tr>
              <td className="p-2 font-medium" colSpan={2}>
                Total allocated
                {paymentTotal !== undefined && (
                  <span className="ml-2 text-muted-foreground font-normal">
                    (of {fmt(paymentTotal)})
                  </span>
                )}
              </td>
              <td
                className={`p-2 text-right font-semibold ${
                  paymentTotal !== undefined && runningTotal > paymentTotal
                    ? "text-destructive"
                    : ""
                }`}
                data-testid="allocation-total"
              >
                {fmt(runningTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
